import React, { useCallback } from "react"
import { useColorModeValue } from "@mallardbay/lib-react-components"

import { TEST_IDS } from "~config/constants"
import { navigateToExternalUrl } from "~utils/helpers/navigation-helpers"
import { trackAppPromptCtaClicked } from "~utils/event-tracking/event-tracking"
import type { AppPromptFieldsFragment } from "~graphql/app-prompts.generated"
import ActionMessageContent from "~components/shared/action-message-content"

type Props = {
    readonly prompt: AppPromptFieldsFragment
}

export default function AppPromptContent({ prompt }: Props) {
    const imageSrc = useColorModeValue(
        prompt.images?.light,
        prompt.images?.dark
    )
    const image = imageSrc
        ? {
              src: imageSrc,
              alt: prompt.title,
              testId: TEST_IDS.APP_PROMPT_IMAGE,
          }
        : undefined

    const onCtaClick = useHandleCtaClick(prompt)

    return (
        <ActionMessageContent
            title={prompt.title}
            message={prompt.message}
            image={image}
            titleTestId={TEST_IDS.APP_PROMPT_TITLE}
            messageTestId={TEST_IDS.APP_PROMPT_MESSAGE}
            buttonLabel={prompt.cta?.url ? prompt.cta.label : undefined}
            buttonTestId={TEST_IDS.APP_PROMPT_CTA}
            onButtonClick={prompt.cta?.url ? onCtaClick : undefined}
        />
    )
}

export function useHasPromptImage(prompt: AppPromptFieldsFragment) {
    const imageSrc = useColorModeValue(
        prompt.images?.light,
        prompt.images?.dark
    )

    return Boolean(imageSrc)
}

function useHandleCtaClick(prompt: AppPromptFieldsFragment) {
    return useCallback(() => {
        const ctaUrl = prompt.cta?.url
        if (!ctaUrl) return

        trackAppPromptCtaClicked({
            promptId: prompt.id,
            promptType: prompt.type,
            severity: prompt.severity,
            ctaUrl,
        })
        navigateToExternalUrl({ url: ctaUrl })
    }, [prompt.id, prompt.type, prompt.severity, prompt.cta?.url])
}
